import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { styled, keyframes } from '@mui/material/styles';
import { Box, IconButton, Stack, Typography, useMediaQuery } from '@mui/material';
// components
import Page from '../Page';
import Iconify from '../Iconify';
import StoreBadges from '../StoreBadges';
// config
import { APP_NAME } from '../../config';

// ---------------------------------------------------------------------
// AuthShell — premium dark canvas.
//  Desktop: gold-aurora showcase (device + glass pills) | frosted form card
//  Mobile : single frosted card on black, logo up top
// ---------------------------------------------------------------------

const GOLD = '#F8C736';
const INK = '#0B0B0C';

const floatY = keyframes`
  0%,100% { transform: translateY(0); }
  50%     { transform: translateY(-14px); }
`;
const pulse = keyframes`
  0%,100% { opacity: .55; transform: scale(1); }
  50%     { opacity: .9;  transform: scale(1.08); }
`;
const rise = keyframes`
  from { opacity: 0; transform: translateY(26px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Root = styled('main')(({ theme }) => ({
  position: 'relative',
  minHeight: '100vh',
  width: '100%',
  maxWidth: '100vw',
  overflowX: 'hidden',
  display: 'grid',
  gridTemplateColumns: 'minmax(0,1fr)',
  background: INK,
  color: '#fff',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background:
      'radial-gradient(60% 55% at 15% 10%, rgba(248,199,54,0.22), transparent 60%),' +
      'radial-gradient(50% 50% at 95% 90%, rgba(248,199,54,0.14), transparent 60%),' +
      'radial-gradient(40% 40% at 80% 8%, rgba(203,154,30,0.12), transparent 60%)',
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    opacity: 0.04,
    backgroundImage:
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
    pointerEvents: 'none',
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'minmax(0, 52fr) minmax(0, 48fr)',
  },
}));

// ---------- LEFT showcase (desktop) ----------

const Showcase = styled('aside')(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('md')]: {
    position: 'relative',
    zIndex: 1,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: theme.spacing(7, 6),
    borderRight: '1px solid rgba(255,255,255,0.06)',
  },
}));

const DotGrid = styled('div')({
  position: 'absolute',
  inset: 0,
  opacity: 0.5,
  backgroundImage:
    'radial-gradient(circle at center, rgba(248,199,54,0.16) 1px, transparent 1.6px)',
  backgroundSize: '26px 26px',
  WebkitMaskImage: 'radial-gradient(ellipse 75% 65% at 40% 45%, black, transparent 78%)',
  maskImage: 'radial-gradient(ellipse 75% 65% at 40% 45%, black, transparent 78%)',
  pointerEvents: 'none',
});

const Pill = styled('div')({
  position: 'absolute',
  zIndex: 3,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 13px',
  borderRadius: 16,
  background: 'rgba(255,255,255,0.07)',
  border: '1px solid rgba(255,255,255,0.12)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  boxShadow: '0 12px 30px -12px rgba(0,0,0,0.6)',
  animation: `${floatY} 6s ease-in-out infinite`,
});

// ---------- RIGHT form ----------

const FormSide = styled('section')(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  minWidth: 0,
  width: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(5, 2.5),
  [theme.breakpoints.up('sm')]: { padding: theme.spacing(6, 5) },
  [theme.breakpoints.up('md')]: { padding: theme.spacing(6, 7) },
}));

const GlassCard = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: 460,
  boxSizing: 'border-box',
  padding: theme.spacing(4, 3),
  borderRadius: 26,
  background: 'rgba(255,255,255,0.045)',
  border: '1px solid rgba(255,255,255,0.09)',
  backdropFilter: 'blur(22px)',
  WebkitBackdropFilter: 'blur(22px)',
  boxShadow: '0 30px 70px -30px rgba(0,0,0,0.8)',
  animation: `${rise} 700ms cubic-bezier(0.16,1,0.3,1) both`,
  [theme.breakpoints.up('sm')]: { padding: theme.spacing(5, 4.5) },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '12%',
    right: '12%',
    height: 1,
    background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
  },
}));

const BackButton = styled(IconButton)({
  width: 40,
  height: 40,
  borderRadius: 999,
  border: '1px solid rgba(255,255,255,0.14)',
  color: 'rgba(255,255,255,0.85)',
  '&:hover': { background: 'rgba(255,255,255,0.06)' },
});

function BrandMark({ size = 34 }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.2}>
      <Box
        component="img"
        src="/logo_icon.webp"
        alt="Bilalsadasub"
        sx={{ width: size, height: size, borderRadius: 1.5, objectFit: 'contain' }}
      />
      <Typography sx={{ fontWeight: 800, letterSpacing: '-0.4px', fontSize: '1.05rem', color: GOLD }}>
        {APP_NAME}
      </Typography>
    </Stack>
  );
}
BrandMark.propTypes = { size: PropTypes.number };

// ---------------------------------------------------------------------

AuthShell.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.node,
  children: PropTypes.node.isRequired,
  footer: PropTypes.node,
  pageTitle: PropTypes.string,
  backTo: PropTypes.string,
  showBack: PropTypes.bool,
};

export default function AuthShell({
  title,
  subtitle,
  children,
  footer,
  pageTitle,
  backTo,
  showBack = true,
}) {
  const navigate = useNavigate();
  const onBack = () => (backTo ? navigate(backTo) : navigate(-1));
  useMediaQuery((t) => t.breakpoints.up('md'));

  return (
    <Page title={pageTitle || title} sx={{ height: 1 }}>
      <Root>
        {/* LEFT — showcase */}
        <Showcase>
          <DotGrid />
          <Box
            sx={{
              position: 'absolute',
              width: 460,
              height: 460,
              top: -120,
              left: -100,
              borderRadius: '50%',
              background: 'rgba(248,199,54,0.30)',
              filter: 'blur(130px)',
              animation: `${pulse} 7s ease-in-out infinite`,
              pointerEvents: 'none',
            }}
          />

          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <BrandMark size={40} />
          </Box>

          {/* headline + device */}
          <Box sx={{ position: 'relative', zIndex: 2, my: 4 }}>
            <Typography
              sx={{
                fontWeight: 800,
                letterSpacing: '-1.6px',
                lineHeight: 1.02,
                fontSize: { md: '2.9rem', lg: '3.5rem' },
                color: '#fff',
              }}
            >
              Everything money,
              <br />
              in one{' '}
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(90deg, ${GOLD}, #FDE68A, ${GOLD})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                gold
              </Box>{' '}
              app.
            </Typography>
            <Typography sx={{ mt: 2.5, fontSize: '1rem', lineHeight: 1.65, color: 'rgba(255,255,255,0.62)', maxWidth: 400 }}>
              Airtime, data, bills, cable and crypto — the fastest way to pay for
              everything digital in Nigeria.
            </Typography>

            {/* floating device */}
            <Box sx={{ position: 'relative', mt: 5, display: 'flex', justifyContent: 'center' }}>
              <Pill sx={{ top: 6, left: { md: 8, lg: 24 } }}>
                <Iconify icon="mdi:trending-up" width={17} height={17} sx={{ color: '#4ade80' }} />
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>Crypto</Typography>
                  <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>+2.4% today</Typography>
                </Box>
              </Pill>
              <Pill sx={{ bottom: 40, right: { md: 4, lg: 18 }, animationDelay: '1.1s' }}>
                <Iconify icon="mdi:wallet" width={17} height={17} sx={{ color: GOLD }} />
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>Wallet funded</Typography>
                  <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>+₦50,000</Typography>
                </Box>
              </Pill>
              <Box
                component="img"
                src="/dashboard-image.webp"
                alt="Bilalsadasub app"
                sx={{
                  position: 'relative',
                  zIndex: 2,
                  width: { md: 220, lg: 260 },
                  height: 'auto',
                  filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.6))',
                  animation: `${floatY} 6s ease-in-out infinite`,
                }}
              />
            </Box>
          </Box>

          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#4ade80', boxShadow: '0 0 12px rgba(74,222,128,0.8)' }} />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
              Trusted by millions of Nigerians · NDPR compliant
            </Typography>
          </Stack>
        </Showcase>

        {/* RIGHT — frosted form */}
        <FormSide>
          <GlassCard>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3.5 }}>
              {showBack ? (
                <BackButton onClick={onBack} aria-label="Back">
                  <Iconify icon="eva:arrow-ios-back-fill" width={20} height={20} />
                </BackButton>
              ) : (
                <BrandMark size={30} />
              )}
              <Box sx={{ width: 40 }} />
            </Stack>

            <Box sx={{ mb: 3.5 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.7px',
                  lineHeight: 1.14,
                  fontSize: { xs: '1.7rem', sm: '2rem' },
                  color: '#fff',
                }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography sx={{ mt: 1.25, fontSize: '0.95rem', lineHeight: 1.55, color: 'rgba(255,255,255,0.6)' }}>
                  {subtitle}
                </Typography>
              )}
            </Box>

            <Box>{children}</Box>

            {footer && <Box sx={{ mt: 3.5, textAlign: 'center' }}>{footer}</Box>}

            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
              <StoreBadges caption="Prefer the mobile app? Get Bilalsadasub on:" />
            </Box>
          </GlassCard>
        </FormSide>
      </Root>
    </Page>
  );
}
